// src/rest/info/general.ts

import type { Hyperliquid } from '../../index'
import type {
  AllMids,
  CandleSnapshot,
  Delegation,
  DelegatorHistoryEntry,
  DelegatorReward,
  DelegatorSummary,
  ExtraAgent,
  FrontendOpenOrders,
  HistoricalOrder,
  L2Book,
  LegalCheck,
  MultiSigSigners,
  OrderStatus,
  PortfolioPeriods,
  PreTransferCheck,
  Referral,
  SubAccount,
  TwapHistory,
  TwapSliceFill,
  UserFees,
  UserFills,
  UserOpenOrders,
  UserRateLimit,
  UserRole,
  ValidatorSummary,
  VaultDetails,
  VaultEquity,
  VaultSummary,
} from '../../types'
import { InfoType } from '../../types/constants'
import type { HttpApi } from '../../utils/helpers'
import type { SymbolConversion } from '../../utils/symbolConversion'

export class GeneralInfoAPI {
  private parent: Hyperliquid

  constructor(private httpApi: HttpApi, private symbolConversion: SymbolConversion, parent: Hyperliquid) {
    this.parent = parent
  }

  async getAllMids(rawResponse = false): Promise<AllMids> {
    const response = await this.httpApi.makeRequest<AllMids>({ type: InfoType.ALL_MIDS })

    if (rawResponse) {
      return response
    }
    const convertedResponse: any = {}
    for (const [key, value] of Object.entries(response)) {
      const convertedKey = await this.symbolConversion.convertSymbol(key)
      const convertedValue = Number.parseFloat(value as string)
      convertedResponse[convertedKey] = convertedValue
    }
    return convertedResponse
  }

  async getUserOpenOrders(user: string, rawResponse = false): Promise<UserOpenOrders> {
    await this.parent.ensureInitialized()
    const response = await this.httpApi.makeRequest({ type: InfoType.OPEN_ORDERS, user })
    return rawResponse ? response : await this.symbolConversion.convertResponse(response)
  }

  async getFrontendOpenOrders(user: string, rawResponse = false): Promise<FrontendOpenOrders> {
    const response = await this.httpApi.makeRequest({ type: InfoType.FRONTEND_OPEN_ORDERS, user }, 20)
    return rawResponse ? response : await this.symbolConversion.convertResponse(response)
  }

  async getUserFills(user: string, rawResponse = false): Promise<UserFills> {
    const response = await this.httpApi.makeRequest(
      { type: InfoType.USER_FILLS, user, aggregateByTime: true },
      20,
    )
    return rawResponse ? response : await this.symbolConversion.convertResponse(response)
  }

  async getUserFillsByTime(
    user: string,
    startTime: number,
    endTime?: number,
    rawResponse = false,
  ): Promise<UserFills> {
    const params: { user: string, startTime: number, type: string, endTime?: number } = {
      user,
      startTime: Math.round(startTime),
      type: InfoType.USER_FILLS_BY_TIME,
    }

    if (endTime) {
      params.endTime = Math.round(endTime)
    }

    const response = await this.httpApi.makeRequest(params, 20)
    return rawResponse ? response : await this.symbolConversion.convertResponse(response)
  }

  async getUserRateLimit(user: string, rawResponse = false): Promise<UserRateLimit> {
    const response = await this.httpApi.makeRequest({ type: InfoType.USER_RATE_LIMIT, user }, 20)
    return rawResponse ? response : await this.symbolConversion.convertResponse(response)
  }

  async getOrderStatus(
    user: string,
    oid: number | string,
    rawResponse = false,
  ): Promise<OrderStatus> {
    const response = await this.httpApi.makeRequest({
      type: InfoType.ORDER_STATUS,
      user,
      oid,
    })
    return rawResponse ? response : await this.symbolConversion.convertResponse(response)
  }

  async getL2Book(
    coin: string,
    rawResponse = false,
    nSigFigs = 5,
    mantissa: number,
  ): Promise<L2Book> {
    const response = await this.httpApi.makeRequest({
      type: InfoType.L2_BOOK,
      coin: await this.symbolConversion.convertSymbol(coin, 'reverse'),
      nSigFigs,
      mantissa,
    })
    return rawResponse ? response : await this.symbolConversion.convertResponse(response)
  }

  async getCandleSnapshot(
    coin: string,
    interval: string,
    startTime: number,
    endTime: number,
    rawResponse = false,
  ): Promise<CandleSnapshot> {
    const response = await this.httpApi.makeRequest({
      type: InfoType.CANDLE_SNAPSHOT,
      req: {
        coin: await this.symbolConversion.convertSymbol(coin, 'reverse'),
        interval,
        startTime,
        endTime,
      },
    })

    return rawResponse ? response : await this.symbolConversion.convertResponse(response, ['s'])
  }

  // Add these methods to src/rest/info/general.ts

  async getMaxBuilderFee(user: string, builder: string, rawResponse = false): Promise<number> {
    const response = await this.httpApi.makeRequest({
      type: InfoType.MAX_BUILDER_FEE,
      user,
      builder,
    })
    return rawResponse ? response : this.symbolConversion.convertToNumber(response)
  }

  async getHistoricalOrders(user: string, rawResponse = false): Promise<HistoricalOrder[]> {
    const response = await this.httpApi.makeRequest({
      type: InfoType.HISTORICAL_ORDERS,
      user,
    })
    return rawResponse ? response : await this.symbolConversion.convertResponse(response)
  }

  async getUserTwapSliceFills(user: string, rawResponse = false): Promise<TwapSliceFill[]> {
    const response = await this.httpApi.makeRequest({
      type: InfoType.USER_TWAP_SLICE_FILLS,
      user,
    })
    return rawResponse ? response : await this.symbolConversion.convertResponse(response)
  }

  async getSubAccounts(user: string, rawResponse = false): Promise<SubAccount[]> {
    const response = await this.httpApi.makeRequest({
      type: InfoType.SUB_ACCOUNTS,
      user,
    })
    return rawResponse ? response : await this.symbolConversion.convertResponse(response)
  }

  async getVaultDetails(
    vaultAddress: string,
    user?: string,
    rawResponse = false,
  ): Promise<VaultDetails> {
    const params: any = {
      type: InfoType.VAULT_DETAILS,
      vaultAddress,
    }

    if (user) {
      params.user = user
    }

    const response = await this.httpApi.makeRequest(params)
    return rawResponse ? response : await this.symbolConversion.convertResponse(response)
  }

  async getUserVaultEquities(user: string, rawResponse = false): Promise<VaultEquity[]> {
    const response = await this.httpApi.makeRequest({
      type: InfoType.USER_VAULT_EQUITIES,
      user,
    })
    return rawResponse ? response : await this.symbolConversion.convertResponse(response)
  }

  async getUserRole(user: string, rawResponse = false): Promise<UserRole> {
    const response = await this.httpApi.makeRequest({
      type: InfoType.USER_ROLE,
      user,
    })
    return rawResponse ? response : await this.symbolConversion.convertResponse(response)
  }

  async getDelegations(user: string, rawResponse = false): Promise<Delegation[]> {
    const response = await this.httpApi.makeRequest({
      type: InfoType.DELEGATIONS,
      user,
    })
    return rawResponse ? response : await this.symbolConversion.convertResponse(response)
  }

  async getDelegatorSummary(user: string, rawResponse = false): Promise<DelegatorSummary> {
    const response = await this.httpApi.makeRequest({
      type: InfoType.DELEGATOR_SUMMARY,
      user,
    })
    return rawResponse ? response : await this.symbolConversion.convertResponse(response)
  }

  async getDelegatorHistory(user: string, rawResponse = false): Promise<DelegatorHistoryEntry[]> {
    const response = await this.httpApi.makeRequest({
      type: InfoType.DELEGATOR_HISTORY,
      user,
    })
    return rawResponse ? response : await this.symbolConversion.convertResponse(response)
  }

  async getDelegatorRewards(user: string, rawResponse = false): Promise<DelegatorReward[]> {
    const response = await this.httpApi.makeRequest({
      type: InfoType.DELEGATOR_REWARDS,
      user,
    })
    return rawResponse ? response : await this.symbolConversion.convertResponse(response)
  }

  async validatorSummaries(rawResponse = false): Promise<ValidatorSummary[]> {
    const response = await this.httpApi.makeRequest({
      type: InfoType.VALIDATOR_SUMMARIES,
    })
    return rawResponse ? response : await this.symbolConversion.convertResponse(response)
  }

  async vaultSummaries(rawResponse = false): Promise<VaultSummary[]> {
    const response = await this.httpApi.makeRequest({
      type: InfoType.VAULT_SUMMARIES,
    })
    return rawResponse ? response : await this.symbolConversion.convertResponse(response)
  }

  async userFees(user: string, rawResponse = false): Promise<UserFees> {
    const response = await this.httpApi.makeRequest({
      type: InfoType.USER_FEES,
      user,
    })
    return rawResponse ? response : await this.symbolConversion.convertResponse(response)
  }

  async portfolio(user: string, rawResponse = false): Promise<PortfolioPeriods> {
    const response = await this.httpApi.makeRequest({
      type: InfoType.PORTFOLIO,
      user,
    })
    return rawResponse ? response : await this.symbolConversion.convertResponse(response)
  }

  async preTransferCheck(
    user: string,
    source: string,
    rawResponse = false,
  ): Promise<PreTransferCheck> {
    const response = await this.httpApi.makeRequest({
      type: InfoType.PRE_TRANSFER_CHECK,
      user,
      source,
    })
    return rawResponse ? response : await this.symbolConversion.convertResponse(response)
  }

  async referral(user: string, rawResponse = false): Promise<Referral> {
    const response = await this.httpApi.makeRequest({
      type: InfoType.REFERRAL,
      user,
    })
    return rawResponse ? response : await this.symbolConversion.convertResponse(response)
  }

  async extraAgents(user: string, rawResponse = false): Promise<ExtraAgent[]> {
    const response = await this.httpApi.makeRequest({
      type: InfoType.EXTRA_AGENTS,
      user,
    })
    return rawResponse ? response : await this.symbolConversion.convertResponse(response)
  }

  async isVip(user: string, rawResponse = false): Promise<boolean> {
    const response = await this.httpApi.makeRequest({
      type: InfoType.IS_VIP,
      user,
    })
    return rawResponse ? response : await this.symbolConversion.convertResponse(response)
  }

  async legalCheck(user: string, rawResponse = false): Promise<LegalCheck> {
    const response = await this.httpApi.makeRequest({
      type: InfoType.LEGAL_CHECK,
      user,
    })
    return rawResponse ? response : await this.symbolConversion.convertResponse(response)
  }

  async userTwapSliceFillsByTime(
    user: string,
    startTime: number,
    endTime?: number,
    aggregateByTime?: boolean,
    rawResponse = false,
  ): Promise<TwapSliceFill[]> {
    const params: any = {
      type: InfoType.USER_TWAP_SLICE_FILLS_BY_TIME,
      user,
      startTime,
    }

    if (endTime !== undefined)
      params.endTime = endTime
    if (aggregateByTime !== undefined)
      params.aggregateByTime = aggregateByTime

    const response = await this.httpApi.makeRequest(params)
    return rawResponse ? response : await this.symbolConversion.convertResponse(response)
  }

  async twapHistory(user: string, rawResponse = false): Promise<TwapHistory[]> {
    const response = await this.httpApi.makeRequest({
      type: InfoType.TWAP_HISTORY,
      user,
    })
    return rawResponse ? response : await this.symbolConversion.convertResponse(response)
  }

  async userToMultiSigSigners(user: string, rawResponse = false): Promise<MultiSigSigners | null> {
    const response = await this.httpApi.makeRequest({
      type: InfoType.USER_TO_MULTI_SIG_SIGNERS,
      user,
    })
    return rawResponse ? response : await this.symbolConversion.convertResponse(response)
  }
}
